import { Formik, Field, Form, FieldProps } from "formik";
import { useTranslation } from "react-i18next";
import { FormControl, FormErrorMessage } from "@chakra-ui/form-control";
import { Stack, ModalFooter, Textarea, Button, Select } from "@chakra-ui/react";
import { Input } from "@chakra-ui/input";
import { Label } from "../Label";
import { useCallback } from "react";
import { VideoCategory } from "../../types";
import * as Yup from "yup";
import { PrimayButton } from "../Button";
import { NumberInput, NumberInputField } from "@chakra-ui/react";

interface Values {
  id: string | null;
  name: string;
  externalId: string;
  resourceKey: string;
  title?: string;
  description?: string;
  order?: number;
  enabled?: boolean;
}

type VideoCategoryFormProps = {
  onSubmit: (data: any) => any;
  videoCategory?: VideoCategory | null;
  onCancel: () => any;
};

export const VideoCategoryForm: React.FC<VideoCategoryFormProps> = ({
  onSubmit,
  videoCategory,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={{
        id: videoCategory?.id ? videoCategory?.id : null,
        name: videoCategory?.name ? videoCategory?.name : "",
        externalId: videoCategory?.externalId ? videoCategory?.externalId : "",
        resourceKey: videoCategory?.resourceKey
          ? videoCategory?.resourceKey
          : "",
        title: videoCategory?.title ? videoCategory?.title : "",
        description: videoCategory?.description
          ? videoCategory?.description
          : "",
        order: videoCategory?.order != null ? videoCategory?.order : 0,
        enabled: videoCategory?.enabled ? videoCategory?.enabled : false,
      }}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={Yup.object().shape({
        enabled: Yup.boolean().required(
          t("screens.client.clientValidations.genderRequired")
        ),
      })}
    >
      {(props) => (
        <Form
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Field id={"id"} name={"id"}>
            {({ field, form: { setFieldValue } }: FieldProps) => (
              <FormControl>
                <Input
                  size="md"
                  type={"hidden"}
                  name={field.name}
                  value={field.value}
                  onChange={(event) =>
                    setFieldValue(field.name, event.target.value)
                  }
                />
              </FormControl>
            )}
          </Field>
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"title"} name={"title"}>
              {({ field, form: { setFieldValue } }: FieldProps) => (
                <FormControl>
                  <Label>
                    {t("screens.adminSettings.videoCategoryForm.title")}
                  </Label>
                  <Input
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                  />
                </FormControl>
              )}
            </Field>
          </Stack>

          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"description"} name={"description"}>
              {({ field, form: { setFieldValue } }: FieldProps) => (
                <FormControl>
                  <Label>
                    {t("screens.adminSettings.videoCategoryForm.description")}
                  </Label>
                  <Textarea
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                  />
                </FormControl>
              )}
            </Field>
          </Stack>
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"order"} name={"order"}>
              {({ field, form: { setFieldValue } }: FieldProps) => (
                <FormControl>
                  <Label>
                    {t("screens.adminSettings.videoCategoryForm.order")}
                  </Label>
                  <NumberInput name={field.name} value={field.value}>
                    <NumberInputField
                      onChange={(event) =>
                        setFieldValue(
                          field.name,
                          event.target.value && event.target.value.length > 0
                            ? parseInt(event.target.value)
                            : event.target.value
                        )
                      }
                    />
                  </NumberInput>
                </FormControl>
              )}
            </Field>
          </Stack>
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"enabled"} name={"enabled"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label>
                    {t("screens.adminSettings.videoCategoryForm.enabled")}
                  </Label>
                  <Select
                    size="md"
                    name={field.name}
                    value={field.value ? "1" : "0"}
                    onChange={(event) =>
                      setFieldValue(
                        field.name,
                        event.target.value === "1" ? true : false
                      )
                    }
                  >
                    <option value="1">
                      {t("screens.adminSettings.videoCategoryForm.active")}
                    </option>
                    <option value="0">
                      {t("screens.adminSettings.videoCategoryForm.notActive")}
                    </option>
                  </Select>
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Stack>

          <ModalFooter>
            <Button type={"reset"} variant={"ghost"} onClick={onCancel}>
              {t("common.reset")}
            </Button>
            <PrimayButton type={"submit"} isLoading={false}>
              {t("common.save")}
            </PrimayButton>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
};
